import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import React from "react";
import { ContentArchive } from "./useContentArchive";

const GREEN = "#2e7d32";
const AMBER = "#ed6c02";
const RED = "#d32f2f";

const useStyles = makeStyles((theme: any) => ({
  bar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    marginBottom: 10,
    borderRadius: 8,
    background: theme.palette.gradient,
    color: theme.palette.common.white,
  },
  count: { fontWeight: 600, flexGrow: 1, fontSize: 14 },
  action: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.secondary.light,
    textTransform: "none",
    fontWeight: 600,
    "&:hover": { backgroundColor: "#f0ecf5" },
  },
  clear: { color: theme.palette.common.white, textTransform: "none" },

  paper: { "& .MuiDialog-paper": { borderRadius: 12, overflow: "hidden" } },

  // Gradient header bar, matching the step headers used on the editor screens.
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 20px",
    background: theme.palette.gradient,
    color: theme.palette.common.white,
  },
  headerIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 38,
    height: 38,
    borderRadius: "50%",
    backgroundColor: "#ffffff26",
  },
  headerTitle: { fontSize: 16, fontWeight: 700, lineHeight: 1.2 },
  headerSubtitle: { fontSize: 12, color: "#EEEEEEB3" },

  content: { padding: "20px !important", minWidth: 440, maxWidth: 560 },

  // Confirm: the count is the thing the user is being asked to verify, so it leads.
  hero: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "14px 16px",
    borderRadius: 10,
    backgroundColor: "#F6F4F9",
    border: "1px solid #E3DEEA",
  },
  heroCount: {
    minWidth: 52,
    height: 52,
    padding: "0 10px",
    borderRadius: 10,
    background: theme.palette.gradient,
    color: theme.palette.common.white,
    fontSize: 22,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  heroLabel: {
    fontSize: 15,
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  heroHint: { fontSize: 12, color: "#6B6478", marginTop: 2 },

  note: {
    display: "flex",
    gap: 10,
    marginTop: 16,
    padding: "12px 14px",
    borderRadius: 8,
    backgroundColor: "#F3F6FB",
    borderLeft: "3px solid #5B7DB1",
  },
  noteIcon: { color: "#5B7DB1", fontSize: 20 },
  noteText: { fontSize: 12.5, color: "#4A5568", lineHeight: 1.55 },

  // Report: one tile per outcome so the totals read at a glance.
  tiles: { display: "flex", gap: 10 },
  tile: {
    flex: 1,
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid",
    textAlign: "center",
  },
  tileCount: { fontSize: 22, fontWeight: 700, lineHeight: 1.1 },
  tileLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },

  group: {
    marginTop: 14,
    padding: "12px 14px",
    borderRadius: 10,
    borderLeft: "3px solid",
    backgroundColor: "#FAFAFB",
  },
  groupHead: { display: "flex", alignItems: "center", gap: 8 },
  groupTitle: { fontSize: 13.5, fontWeight: 700 },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 10,
    maxHeight: 118,
    overflowY: "auto",
  },
  chip: {
    maxWidth: 220,
    backgroundColor: theme.palette.common.white,
    border: "1px solid #E0DCE6",
    fontSize: 12,
  },
  groupHint: { fontSize: 12, color: "#6B6478", marginTop: 8 },

  actions: { padding: "12px 20px 16px" },
  cancel: { textTransform: "none", color: "#6B6478" },
  confirm: {
    textTransform: "none",
    fontWeight: 600,
    minWidth: 108,
    background: theme.palette.gradient,
    color: theme.palette.common.white,
    "&:hover": { background: theme.palette.gradient, opacity: 0.92 },
    "&.Mui-disabled": { color: "#ffffff99" },
  },
}));

interface Props {
  archive: ContentArchive;
  /** Current page rows, used to turn ids back into names for the report. */
  rows: any[];
  /** Field holding the human-readable name of a row. */
  nameKey?: string;
  getRowId?: (row: any) => string;
}

/** One outcome group in the report: coloured accent, count, and the affected items by name. */
const ResultGroup = ({
  color,
  tint,
  icon,
  title,
  ids,
  nameOf,
  hint,
}: {
  color: string;
  tint: string;
  icon: React.ReactNode;
  title: string;
  ids: string[];
  nameOf: (id: string) => string;
  hint?: string;
}) => {
  const styles = useStyles();
  if (!ids?.length) return null;

  return (
    <Box
      className={styles.group}
      style={{ borderLeftColor: color, backgroundColor: tint }}
    >
      <Box className={styles.groupHead}>
        <Box style={{ color, display: "flex" }}>{icon}</Box>
        <Typography className={styles.groupTitle} style={{ color }}>
          {title} ({ids.length})
        </Typography>
      </Box>
      <Box className={styles.chips}>
        {ids.map((id) => (
          <Chip
            key={id}
            size="small"
            className={styles.chip}
            label={nameOf(id)}
          />
        ))}
      </Box>
      {hint && <Typography className={styles.groupHint}>{hint}</Typography>}
    </Box>
  );
};

/**
 * The selection bar plus the confirm and report dialogs for one list screen. Rendered directly
 * above the table; it shows nothing until rows are selected, so the screen looks unchanged
 * until the user starts a bulk action.
 */
const ArchiveActionBar = ({
  archive,
  rows,
  nameKey = "title",
  getRowId = (row: any) => row?._id,
}: Props) => {
  const styles = useStyles();
  const { archived, selectedIds, label, result } = archive;

  const verb = archived ? "Restore" : "Archive";
  const verbing = archived ? "Restoring" : "Archiving";
  const done = archived ? "restored" : "archived";
  const noun = selectedIds.length === 1 ? label.singular : label.plural;

  const nameById = React.useMemo(() => {
    const map = new Map<string, string>();
    (rows ?? []).forEach((row) => map.set(getRowId(row), row?.[nameKey]));
    return map;
  }, [rows, nameKey, getRowId]);

  // The list refetches as soon as the action finishes, so by the time the report renders the
  // archived rows are already gone from `rows`. Hold the last map from before the action started
  // and read names from that, otherwise the report would list raw ids.
  const namesBeforeAction = React.useRef(nameById);
  React.useEffect(() => {
    if (!archive.confirmOpen && !result) namesBeforeAction.current = nameById;
  }, [nameById, archive.confirmOpen, result]);

  const nameOf = (id: string) =>
    namesBeforeAction.current.get(id) || nameById.get(id) || id;

  const succeeded = result?.successIds?.length ?? 0;
  const skipped = result?.skippedIds?.length ?? 0;
  const failed = result?.failedIds?.length ?? 0;
  const total = succeeded + skipped + failed;

  return (
    <>
      {selectedIds.length > 0 && (
        <Box className={styles.bar}>
          <Typography className={styles.count}>
            {selectedIds.length} {noun} selected
          </Typography>
          <Button className={styles.clear} onClick={archive.clearSelection}>
            Clear
          </Button>
          <Button
            variant="contained"
            className={styles.action}
            startIcon={
              archived ? <UnarchiveOutlinedIcon /> : <ArchiveOutlinedIcon />
            }
            onClick={() => archive.requestAction()}
          >
            {verb}
          </Button>
        </Box>
      )}

      <Dialog
        open={archive.confirmOpen}
        onClose={archive.closeConfirm}
        className={styles.paper}
      >
        <Box className={styles.header}>
          <Box className={styles.headerIcon}>
            {archived ? <UnarchiveOutlinedIcon /> : <ArchiveOutlinedIcon />}
          </Box>
          <Box>
            <Typography className={styles.headerTitle}>
              {verb} content
            </Typography>
            <Typography className={styles.headerSubtitle}>
              Please confirm this action
            </Typography>
          </Box>
        </Box>

        <DialogContent className={styles.content}>
          <Box className={styles.hero}>
            <Box className={styles.heroCount}>{selectedIds.length}</Box>
            <Box>
              <Typography className={styles.heroLabel}>
                {noun} will be {done}
              </Typography>
              <Typography className={styles.heroHint}>
                {archived
                  ? "They return to the main list, still inactive."
                  : "They are hidden from this list and every picker in the CMS."}
              </Typography>
            </Box>
          </Box>

          <Box className={styles.note}>
            <InfoOutlinedIcon className={styles.noteIcon} />
            <Typography className={styles.noteText}>
              {archived
                ? `Restored ${label.plural} come back in the inactive state they were archived in, so nothing goes live until you activate it.`
                : `Nothing is deleted — everything already using these ${label.plural} keeps working, and you can restore them at any time from the archive.`}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions className={styles.actions}>
          <Button onClick={archive.closeConfirm} className={styles.cancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            className={styles.confirm}
            onClick={archive.confirmAction}
            disabled={archive.busy}
            autoFocus
            startIcon={
              archive.busy ? (
                <CircularProgress size={15} color="inherit" />
              ) : undefined
            }
          >
            {archive.busy ? `${verbing}…` : verb}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!result}
        onClose={archive.closeResult}
        className={styles.paper}
      >
        <Box className={styles.header}>
          <Box className={styles.headerIcon}>
            {succeeded === total ? (
              <CheckCircleOutlineIcon />
            ) : (
              <InfoOutlinedIcon />
            )}
          </Box>
          <Box>
            <Typography className={styles.headerTitle}>
              {verb} report
            </Typography>
            <Typography className={styles.headerSubtitle}>
              {succeeded} of {total}{" "}
              {total === 1 ? label.singular : label.plural} {done}
            </Typography>
          </Box>
        </Box>

        <DialogContent className={styles.content}>
          <Box className={styles.tiles}>
            <Box
              className={styles.tile}
              style={{ borderColor: "#C8E6C9", backgroundColor: "#F1F8F2" }}
            >
              <Typography className={styles.tileCount} style={{ color: GREEN }}>
                {succeeded}
              </Typography>
              <Typography className={styles.tileLabel} style={{ color: GREEN }}>
                {done}
              </Typography>
            </Box>
            <Box
              className={styles.tile}
              style={{ borderColor: "#FFE0B2", backgroundColor: "#FFF8F0" }}
            >
              <Typography className={styles.tileCount} style={{ color: AMBER }}>
                {skipped}
              </Typography>
              <Typography className={styles.tileLabel} style={{ color: AMBER }}>
                Skipped
              </Typography>
            </Box>
            <Box
              className={styles.tile}
              style={{ borderColor: "#FFCDD2", backgroundColor: "#FEF4F4" }}
            >
              <Typography className={styles.tileCount} style={{ color: RED }}>
                {failed}
              </Typography>
              <Typography className={styles.tileLabel} style={{ color: RED }}>
                Not processed
              </Typography>
            </Box>
          </Box>

          <ResultGroup
            color={GREEN}
            tint="#F1F8F2"
            icon={<CheckCircleOutlineIcon fontSize="small" />}
            title={done.charAt(0).toUpperCase() + done.slice(1)}
            ids={result?.successIds ?? []}
            nameOf={nameOf}
          />

          <ResultGroup
            color={AMBER}
            tint="#FFF8F0"
            icon={<RemoveCircleOutlineIcon fontSize="small" />}
            title="Still live"
            ids={result?.skippedIds ?? []}
            nameOf={nameOf}
            hint={`Deactivate these ${label.plural} first, then archive them.`}
          />

          <ResultGroup
            color={RED}
            tint="#FEF4F4"
            icon={<ErrorOutlineIcon fontSize="small" />}
            title="Not processed"
            ids={result?.failedIds ?? []}
            nameOf={nameOf}
            hint={`These ${label.plural} were already ${done} or no longer exist.`}
          />
        </DialogContent>

        <DialogActions className={styles.actions}>
          <Button
            variant="contained"
            className={styles.confirm}
            onClick={archive.closeResult}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ArchiveActionBar;
